export const geometryTopics = [
  {
    id: 1,
    title: "Points, Lines, Planes & Angles",
    standards: {
      commonCore: ["G-CO.A.1"],
      nctm: ["Geometry - Analyze characteristics and properties of geometric shapes"],
      states: {
        ny: ["G.G.1"],
        oh: ["G.CO.1"],
        pa: ["CC.2.3.HS.A.1"],
        va: ["G.1"],
        tx: ["G.3A"]
      }
    },
    content: `
# Points, Lines, Planes & Angles

## What You'll Learn
Understanding the building blocks of geometry - points, lines, planes, and angles.

## Mr. Augustine's Easy Explanation

**Point:** A dot with no size, just a location (like point A).

**Line:** Goes forever in both directions. We write it as \\(\\overleftrightarrow{AB}\\).

**Line Segment:** A piece of a line with two endpoints: \\(\\overline{AB}\\).

**Ray:** Starts at one point and goes forever in one direction: \\(\\overrightarrow{AB}\\).

**Plane:** A flat surface that goes on forever (like an endless sheet of paper).

**Angle:** Formed when two rays share the same starting point (vertex).

### Types of Angles
- **Acute:** Less than 90° (a cute little angle)
- **Right:** Exactly 90° (corner of a book)
- **Obtuse:** Between 90° and 180° (bigger than right)
- **Straight:** Exactly 180° (straight line)

### Special Angle Pairs
- **Vertical Angles:** Opposite angles when lines cross - ALWAYS equal!
- **Complementary:** Add to 90°
- **Supplementary:** Add to 180°
- **Adjacent:** Share a side and vertex

## Practice Problems

**1.** What is the complement of 25°?
**2.** What is the supplement of 45°?
**3.** Two lines intersect at 75°. Find the vertical angle.
**4.** If ∠A and ∠B are complementary and ∠A = 62°, find ∠B.
**5.** If ∠X and ∠Y are supplementary and ∠X = 95°, find ∠Y.
**6.** Name the angle type: 45°
**7.** Name the angle type: 135°
**8.** Two angles are complementary. One is twice the other. Find both.
**9.** Two angles are supplementary. One is 30° more than the other. Find both.
**10.** Two lines intersect. One angle is 3x and its vertical angle is 2x + 20. Find x.
**11.** ∠A and ∠B are complementary. ∠A = 2x + 10, ∠B = x + 20. Find both angles.
**12.** ∠P and ∠Q are supplementary. ∠P = 4x - 10, ∠Q = 2x + 10. Find both angles.
**13.** Three angles form a straight line: 45°, 60°, and x. Find x.
**14.** Two intersecting lines form angles (5x - 10)° and (3x + 30)°. Find all four angles.
**15.** The complement of an angle is 15° less than twice the angle. Find it.
**16.** The supplement of an angle is 40° more than three times the angle. Find it.
**17.** Vertical angles: one is (7x - 20)°, the other is (5x + 10)°. Find each angle.
**18.** ∠A and ∠B are complementary. ∠B and ∠C are supplementary. If ∠A = 35°, find ∠C.
**19.** Two lines intersect. One angle is (8x + 5)° and its supplement is (12x - 15)°. Find all angles.
**20.** Three rays from one point form angles x°, 2x°, and 3x° on a straight line. Find each.

## Solutions

**1.** 65° **2.** 135° **3.** 75° **4.** 28° **5.** 85° **6.** Acute **7.** Obtuse
**8.** 30° and 60° **9.** 75° and 105° **10.** x = 20 **11.** 50° and 40°
**12.** 110° and 70° **13.** 75° **14.** 90°, 90°, 90°, 90° **15.** 35°
**16.** 35° **17.** 85° each **18.** 125° **19.** 81°, 99°, 81°, 99° **20.** 30°, 60°, 90°
    `
  },
  {
    id: 2,
    title: "Parallel & Perpendicular Lines",
    standards: {
      commonCore: ["G-CO.A.1", "G-CO.C.9"],
      nctm: ["Geometry - Analyze characteristics and properties of geometric shapes"],
      states: {
        ny: ["G.G.2", "G.G.3"],
        oh: ["G.CO.9"],
        pa: ["CC.2.3.HS.A.2"],
        va: ["G.2"],
        tx: ["G.3B"]
      }
    },
    content: `
# Parallel & Perpendicular Lines

## What You'll Learn
Parallel lines (never meet) and perpendicular lines (meet at 90°), plus special angles!

## Mr. Augustine's Easy Explanation

**Parallel Lines (∥):** Like railroad tracks - run side by side forever.

**Perpendicular Lines (⊥):** Meet at exactly 90° (like a plus sign).

**Transversal:** A line crossing two or more parallel lines.

### Special Angles with Parallel Lines

**Corresponding Angles:** Same position at each intersection - EQUAL!

**Alternate Interior Angles:** Inside, opposite sides - EQUAL!

**Alternate Exterior Angles:** Outside, opposite sides - EQUAL!

**Consecutive Interior Angles:** Inside, same side - add to 180°!

### Slopes
- **Parallel lines:** Same slope
- **Perpendicular lines:** Slopes are negative reciprocals (flip and change sign)

## Practice Problems

**1.** Parallel lines cut by transversal. One angle is 55°. Find the corresponding angle.
**2.** What angle do perpendicular lines form?
**3.** Alternate interior angle is 80°. Find the other.
**4.** What's the slope of a line parallel to y = 5x + 2?
**5.** What's the slope perpendicular to y = 2x - 3?
**6.** Consecutive interior angle is 100°. Find the other.
**7.** Are slopes m = 3 and m = -1/3 parallel, perpendicular, or neither?
**8.** One angle is (2x + 10)°, corresponding is (3x - 20)°. Find x and angles.
**9.** Alternate interior: (5x)° and (3x + 40)°. Find x and angles.
**10.** Line parallel to y = -2x + 7 through (0, 3). Find equation.
**11.** Line perpendicular to y = (1/2)x + 4 through (0, -1). Find equation.
**12.** Consecutive interior: (4x + 20)° and (6x - 10)°. Find x and angles.
**13.** Are y = 3x + 1 and y = 3x - 5 parallel, perpendicular, or neither?
**14.** Are y = (2/3)x + 1 and y = (-3/2)x + 4 parallel, perpendicular, or neither?
**15.** Alternate exterior: (7x - 15)° and (5x + 25)°. Find all 8 angles.
**16.** Line parallel to 2x + 3y = 6 through (3, 4). Find equation.
**17.** Line perpendicular to 4x - y = 8 through (2, 5). Find equation.
**18.** Three parallel lines, transversal creates 65° at first. Find corresponding at second.
**19.** Line m through (1,3) and (4,9). Line n through (2,5) and (5,11). Parallel, perpendicular, or neither?
**20.** Two parallel lines, two transversals form triangle with angles 50° and 70°. Find third angle.

## Solutions

**1.** 55° **2.** 90° **3.** 80° **4.** m = 5 **5.** m = -1/2 **6.** 80° **7.** Perpendicular
**8.** x = 30, 70° **9.** x = 20, 100° **10.** y = -2x + 3 **11.** y = -2x - 1
**12.** x = 17, 88° and 92° **13.** Parallel **14.** Perpendicular **15.** Four 125°, four 55°
**16.** 2x + 3y = 18 **17.** x + 4y = 22 **18.** 65° **19.** Parallel **20.** 60°
    `
  },
  {
    id: 3,
    title: "Triangle Congruence (SSS, SAS, ASA, AAS)",
    standards: {
      commonCore: ["G-CO.B.7", "G-CO.B.8"],
      nctm: ["Geometry - Apply transformations and use symmetry"],
      states: {
        ny: ["G.G.27", "G.G.28"],
        oh: ["G.CO.7", "G.CO.8"],
        pa: ["CC.2.3.HS.A.3"],
        va: ["G.3"],
        tx: ["G.5A", "G.5B"]
      }
    },
    content: `
# Triangle Congruence (SSS, SAS, ASA, AAS)

## What You'll Learn
How to prove triangles are exactly the same size and shape using four shortcuts!

## Mr. Augustine's Easy Explanation

**Congruent (≅):** Same size AND shape. Symbol: ≅

### Four Congruence Shortcuts

**1. SSS (Side-Side-Side):** All three sides equal → triangles congruent

**2. SAS (Side-Angle-Side):** Two sides and included angle equal → congruent
   - Angle MUST be between the sides!

**3. ASA (Angle-Side-Angle):** Two angles and included side equal → congruent
   - Side MUST be between the angles!

**4. AAS (Angle-Angle-Side):** Two angles and non-included side → congruent

### What Doesn't Work
- **AAA:** Only proves similarity, not congruence
- **SSA:** Ambiguous (except right triangles)

### CPCTC
Once triangles are congruent: **Corresponding Parts of Congruent Triangles are Congruent**

## Practice Problems

**1.** Sides 3, 4, 5 vs. sides 3, 4, 5. Congruent? Method?
**2.** AB = DE, BC = EF, ∠B = ∠E (included). What method?
**3.** ∠A = ∠D, ∠B = ∠E, AB = DE (included). What method?
**4.** True/False: AAA proves congruence.
**5.** △ABC ≅ △DEF. If AB = 8, what is DE?
**6.** △ABC ≅ △DEF. If ∠A = 45°, what is ∠D?
**7.** ∠A = ∠D, ∠B = ∠E, BC = EF. What method?
**8.** Sides 7, 9, 11 vs. 7, 9, 11. Prove congruence.
**9.** AB = DE = 12, ∠A = ∠D = 55°, AC = DF = 10. Prove congruence.
**10.** Right angles at C and E, AB = DE = 6, BC = EF = 8. Prove congruence.
**11.** △ABC ≅ △DEF by ASA. ∠C = 70°, BC = 15. Find ∠F and EF.
**12.** Can you prove with AB = DE, BC = EF, ∠A = ∠D? Why/why not?
**13.** ∠A = ∠D = 40°, ∠B = ∠E = 60°, AC = DF = 14. Prove congruence.
**14.** Right triangles at B and E. AB = DE, BC = EF. Congruent? Method?
**15.** Isosceles △ABC: AB = AC. D is midpoint of BC. Prove △ABD ≅ △ACD.
**16.** Triangles share AB. AC = AD, ∠CAB = ∠DAB. Prove △ABC ≅ △ABD.
**17.** Rectangle ABCD, diagonal AC. Prove △ABC ≅ △CDA.
**18.** △ABC ≅ △DEF. Perimeter = 30. AB = 8, BC = 12. Find all sides of △DEF.
**19.** AB = DE, ∠A = ∠D, ∠C = ∠F. Can you prove? Method?
**20.** Parallelogram ABCD, diagonal BD. Prove △ABD ≅ △CDB two ways.

## Solutions

**1.** Yes, SSS **2.** SAS **3.** ASA **4.** False **5.** 8 **6.** 45° **7.** AAS
**8.** SSS **9.** SAS **10.** SAS **11.** ∠F = 70°, EF = 15 **12.** No, SSA invalid
**13.** AAS **14.** Yes, SAS **15.** SSS (AB=AC, BD=DC, AD=AD) **16.** SAS
**17.** SSS **18.** 8, 12, 10 **19.** Yes, AAS **20.** SSS or SAS
    `
  },
  {
    id: 4,
    title: "Triangle Similarity (AA, SAS, SSS)",
    standards: {
      commonCore: ["G-SRT.A.2", "G-SRT.A.3"],
      nctm: ["Geometry - Analyze characteristics and properties of geometric shapes"],
      states: {
        ny: ["G.G.44", "G.G.45"],
        oh: ["G.SRT.2", "G.SRT.3"],
        pa: ["CC.2.3.HS.A.4"],
        va: ["G.4"],
        tx: ["G.5C"]
      }
    },
    content: `
# Triangle Similarity (AA, SAS, SSS)

## What You'll Learn
How to prove triangles have the same shape (but not necessarily same size)!

## Mr. Augustine's Easy Explanation

**Similar (~):** Same shape, can be different sizes. All angles equal, sides proportional.

### Three Similarity Shortcuts

**1. AA (Angle-Angle):** Two angles equal → similar (EASIEST!)

**2. SAS~ (Side-Angle-Side):** Two sides proportional, included angle equal → similar

**3. SSS~ (Side-Side-Side):** All three sides proportional → similar

**Scale Factor:** Ratio of corresponding sides

## Practice Problems

**1.** ∠A = ∠D = 40°, ∠B = ∠E = 70°. Similar? Method?
**2.** AB = 5, DE = 15. What's the scale factor?
**3.** Scale factor 3, BC = 4. Find EF.
**4.** True/False: Congruent → similar.
**5.** True/False: Similar → congruent.
**6.** Sides 2,3,4 vs. 4,6,8. Similar? Method?
**7.** ∠A = ∠D, ∠B = ∠E. Similar? Method?
**8.** △ABC ~ △DEF. AB=8, BC=10, AC=12, DE=12. Find EF and DF.
**9.** AB=6, BC=9, ∠B=45°, DE=4, EF=6, ∠E=45°. Similar? Method?
**10.** 5-ft ladder reaches 4 ft. 15-ft ladder at same angle reaches how high?
**11.** Perimeters 24 and 36. AB = 8. Find DE.
**12.** ∠A=55°, ∠C=75°, ∠D=55°, ∠F=75°. Prove similar.
**13.** Sides 5,12,13 vs. 10,24,26. Prove similar, find scale factor.
**14.** AB/DE = BC/EF = AC/DF = 2/3. What can you conclude?
**15.** DE ∥ BC. AD=4, DB=6, AE=6. Find EC.
**16.** △ABC ~ △DEF. Areas 16 and 64. AB=6. Find DE.
**17.** Tree shadow 15 ft, 4-ft post shadow 3 ft. How tall is tree?
**18.** DE ∥ BC. AD=5, DB=3, DE=10. Find BC.
**19.** Scale factor 3:2. Perimeter △ABC = 45. Find perimeter △DEF.
**20.** Right △ABC, altitude CD to hypotenuse. AC=6, BC=8. Find CD.

## Solutions

**1.** Yes, AA **2.** 3 **3.** 12 **4.** True **5.** False **6.** Yes, SSS~ **7.** Yes, AA
**8.** EF=15, DF=18 **9.** Yes, SAS~ **10.** 12 ft **11.** 12 **12.** AA **13.** SSS~, 1/2
**14.** Similar by SSS~ **15.** 9 **16.** 12 **17.** 20 ft **18.** 16 **19.** 30 **20.** 4.8
    `
  },
  {
    id: 5,
    title: "Right Triangles & Trigonometry (SOH-CAH-TOA)",
    standards: {
      commonCore: ["G-SRT.C.6", "G-SRT.C.7", "G-SRT.C.8"],
      nctm: ["Geometry - Use visualization, spatial reasoning, and geometric modeling"],
      states: {
        ny: ["G.G.48", "G.G.49", "G.G.50"],
        oh: ["G.SRT.6", "G.SRT.7", "G.SRT.8"],
        pa: ["CC.2.3.HS.A.7"],
        va: ["G.8"],
        tx: ["G.9A", "G.9B"]
      }
    },
    content: `
# Right Triangles & Trigonometry (SOH-CAH-TOA)

## What You'll Learn
Finding missing sides and angles in right triangles using sine, cosine, and tangent!

## Mr. Augustine's Easy Explanation

**Right Triangle:** One 90° angle. Longest side = hypotenuse.

### The Three Sides (from an angle's view)
- **Hypotenuse:** Longest side (opposite right angle)
- **Opposite:** Across from your angle
- **Adjacent:** Next to your angle (not hypotenuse)

### SOH-CAH-TOA (The Magic Formula!)

**SOH:** sin(θ) = Opposite/Hypotenuse
**CAH:** cos(θ) = Adjacent/Hypotenuse
**TOA:** tan(θ) = Opposite/Adjacent

### Special Right Triangles
**45-45-90:** Ratio 1 : 1 : √2
**30-60-90:** Ratio 1 : √3 : 2

### Pythagorean Theorem
**a² + b² = c²** (c = hypotenuse)

## Practice Problems

**1.** ∠A=30°, AB=10. Find BC using sine.
**2.** ∠A=60°, AB=20. Find AC using cosine.
**3.** ∠A=45°, AC=10. Find BC using tangent.
**4.** Legs 3 and 4. Find hypotenuse.
**5.** 45-45-90 triangle, legs = 6. Find hypotenuse.
**6.** 30-60-90 triangle, short leg = 4. Find hypotenuse.
**7.** BC=5, AC=12. Find ∠A using tangent.
**8.** Ladder at 70°, 15 ft long. How high up wall?
**9.** 100 ft from building, angle 35° to top. Building height?
**10.** ∠A=25°, BC=8. Find AB.
**11.** ∠B=40°, AB=30. Find AC.
**12.** Ramp rises 3 ft over 20 ft. Angle of elevation?
**13.** 30-60-90 triangle, hypotenuse = 18. Find both legs.
**14.** AC=7, BC=24. Find ∠A and ∠B.
**15.** Kite at 80 ft, string at 55°. String length?
**16.** 200-ft cliff, angle of depression 15° to boat. Distance to boat?
**17.** ∠A=35°, perimeter=50, BC=12. Find AB and AC.
**18.** 20-ft ladder, base 8 ft from wall. Angle with ground?
**19.** sin(A)=3/5, AB=25. Find all sides and angles.
**20.** 50 m from building. Angle to top 60°, to window 30°. Window distance from top?

## Solutions

**1.** 5 **2.** 10 **3.** 10 **4.** 5 **5.** 6√2≈8.49 **6.** 8 **7.** 22.62°
**8.** 14.10 ft **9.** 70.02 ft **10.** 18.93 **11.** 19.28 **12.** 8.53° **13.** 9, 9√3
**14.** 73.74°, 16.26° **15.** 97.66 ft **16.** 746.41 ft **17.** AB≈20.92, AC≈17.14
**18.** 66.42° **19.** BC=15, AC=20, ∠A≈36.87°, ∠B≈53.13° **20.** 57.73 m
    `
  }
];

export const stateTestPrep = {
  title: "Preparing for State Tests",
  description: "Comprehensive practice for state standardized geometry tests",
  questions: [
    { id: 1, question: "Two angles are complementary. One is 38°. Find the other.", answer: "52°", solution: "90° - 38° = 52°" },
    { id: 2, question: "Parallel lines, transversal, one angle 115°. Find corresponding angle.", answer: "115°", solution: "Corresponding angles equal" },
    { id: 3, question: "Sides 5,7,9 vs. 5,7,9. Congruent? Method?", answer: "Yes, SSS", solution: "All sides equal" },
    { id: 4, question: "△ABC ~ △DEF, scale 2, AB=6. Find DE.", answer: "12", solution: "DE = 2×6 = 12" },
    { id: 5, question: "Right triangle, ∠A=30°, AB=20. Find BC.", answer: "10", solution: "sin(30°)=BC/20, BC=10" },
    { id: 6, question: "Supplement of 67°?", answer: "113°", solution: "180° - 67° = 113°" },
    { id: 7, question: "Lines intersect at 42°. Vertical angle?", answer: "42°", solution: "Vertical angles equal" },
    { id: 8, question: "45-45-90, legs=8. Hypotenuse?", answer: "8√2≈11.31", solution: "8√2" },
    { id: 9, question: "Slopes m=4 and m=-1/4. Relationship?", answer: "Perpendicular", solution: "4×(-1/4)=-1" },
    { id: 10, question: "∠A=45°, ∠B=65° vs. ∠D=45°, ∠E=65°. Similar?", answer: "Yes, AA", solution: "Two angles equal" },
    { id: 11, question: "Consecutive interior 75°. Find other.", answer: "105°", solution: "180° - 75° = 105°" },
    { id: 12, question: "Right triangle, legs 5 and 12. Hypotenuse?", answer: "13", solution: "5²+12²=169, √169=13" },
    { id: 13, question: "△ABC ≅ △DEF, ∠A=55°. Find ∠D.", answer: "55°", solution: "Corresponding angles equal" },
    { id: 14, question: "30-60-90, short leg=6. Hypotenuse?", answer: "12", solution: "2×6=12" },
    { id: 15, question: "Slope perpendicular to y=3x+5?", answer: "-1/3", solution: "Negative reciprocal" },
    { id: 16, question: "Supplementary, one 40° more. Find both.", answer: "70° and 110°", solution: "x+(x+40)=180" },
    { id: 17, question: "AB=DE=8, ∠B=∠E=60°, BC=EF=10. Congruent? Method?", answer: "Yes, SAS", solution: "Two sides, included angle" },
    { id: 18, question: "Perimeters 30 and 45. Scale factor?", answer: "3/2", solution: "45/30=3/2" },
    { id: 19, question: "BC=7, AC=24. Find ∠A.", answer: "≈16.26°", solution: "tan⁻¹(7/24)" },
    { id: 20, question: "Alternate interior (3x+10)° and (5x-30)°. Find x.", answer: "20", solution: "3x+10=5x-30" },
    { id: 21, question: "92° angle type?", answer: "Obtuse", solution: "Between 90° and 180°" },
    { id: 22, question: "Sides 6,8,10 vs. 3,4,5. Similar? Method?", answer: "Yes, SSS~", solution: "All ratios = 2" },
    { id: 23, question: "Parallel to y=-3x+7 through (0,2)?", answer: "y=-3x+2", solution: "Same slope, b=2" },
    { id: 24, question: "∠A=∠D=50°, ∠B=∠E=70°, BC=EF=12. Congruent? Method?", answer: "Yes, AAS", solution: "Two angles, non-included side" },
    { id: 25, question: "Ladder 65°, 12 ft. Height on wall?", answer: "≈10.88 ft", solution: "12×sin(65°)" },
    { id: 26, question: "Complementary, one 3× other. Find both.", answer: "22.5° and 67.5°", solution: "x+3x=90" },
    { id: 27, question: "30-60-90, long leg=12. Short leg?", answer: "4√3≈6.93", solution: "12/√3=4√3" },
    { id: 28, question: "y=2x+3 and y=2x-5. Relationship?", answer: "Parallel", solution: "Same slope" },
    { id: 29, question: "Scale 1/2, area △ABC=36. Area △DEF?", answer: "9", solution: "(1/2)²×36=9" },
    { id: 30, question: "∠A=40°, AC=15. Find BC.", answer: "≈12.59", solution: "15×tan(40°)" },
    { id: 31, question: "Parallel lines, one angle 48°. Find all 8.", answer: "Four 48°, four 132°", solution: "Corresponding and supplementary" },
    { id: 32, question: "Isosceles AB=AC, ∠B=65°. Find ∠A.", answer: "50°", solution: "180°-65°-65°=50°" },
    { id: 33, question: "Perpendicular to y=(2/3)x+1 through (0,4)?", answer: "y=(-3/2)x+4", solution: "Negative reciprocal slope" },
    { id: 34, question: "△ABC ≅ △DEF, perimeter=42, AB=12, BC=15. Find DF.", answer: "15", solution: "AC=15, DF=AC by CPCTC" },
    { id: 35, question: "80 ft from tree, angle 50°. Tree height?", answer: "≈95.34 ft", solution: "80×tan(50°)" },
    { id: 36, question: "BC=9, AB=15. Find AC.", answer: "12", solution: "√(15²-9²)=12" },
    { id: 37, question: "Linear pair (4x+20)° and (6x-10)°. Find x.", answer: "17", solution: "Sum=180°" },
    { id: 38, question: "△ABC ~ △DEF, AB=10, BC=15, DE=6. Find EF.", answer: "9", solution: "Scale 3/5, EF=9" },
    { id: 39, question: "45-45-90, hypotenuse=10√2. Each leg?", answer: "10", solution: "leg×√2=10√2" },
    { id: 40, question: "sin(A)=4/5, AB=20. Find BC.", answer: "16", solution: "BC/20=4/5" },
    { id: 41, question: "Corresponding (7x-5)° and (5x+15)°. Find angle.", answer: "65°", solution: "x=10, angle=65°" },
    { id: 42, question: "∠A=∠D, AB=DE, ∠B=∠E. Congruent? Method?", answer: "Yes, ASA", solution: "Two angles, included side" },
    { id: 43, question: "15-ft ladder, base 9 ft from wall. Angle with ground?", answer: "≈53.13°", solution: "cos⁻¹(9/15)" },
    { id: 44, question: "Complement of 73°?", answer: "17°", solution: "90°-73°=17°" },
    { id: 45, question: "Sides 10,24,26. Right triangle?", answer: "Yes", solution: "10²+24²=26²" },
    { id: 46, question: "30-60-90, hypotenuse=24. Long leg?", answer: "12√3≈20.78", solution: "Short=12, long=12√3" },
    { id: 47, question: "Slopes m=5 and m=5. Relationship?", answer: "Parallel", solution: "Same slope" },
    { id: 48, question: "Perimeter ratio 2:3, AB=8. Find DE.", answer: "12", solution: "8/DE=2/3" },
    { id: 49, question: "∠A=35°, BC=10. Find AB.", answer: "≈17.43", solution: "10/sin(35°)" },
    { id: 50, question: "Vertical (8x-10)° and (6x+20)°. Find x and angle.", answer: "x=15, 110°", solution: "8x-10=6x+20" },
    { id: 51, question: "DE∥BC, AD=6, DB=4, AE=9. Find EC.", answer: "6", solution: "6/10=9/(9+EC)" },
    { id: 52, question: "Legs ratio 3:4, hypotenuse=25. Find legs.", answer: "15 and 20", solution: "(3x)²+(4x)²=625" },
    { id: 53, question: "Parallel to 3x+2y=6 through (2,3)?", answer: "3x+2y=12", solution: "Same slope -3/2" },
    { id: 54, question: "△ABC ≅ △DEF by SAS, AC=18, ∠C=75°. Find DF and ∠F.", answer: "DF=18, ∠F=75°", solution: "CPCTC" },
    { id: 55, question: "150-ft building, depression 25° to car. Distance?", answer: "≈321.51 ft", solution: "150/tan(25°)" },
    { id: 56, question: "45-45-90, perimeter=10+10√2. Each leg?", answer: "5", solution: "x+x+x√2=10+10√2" },
    { id: 57, question: "Consecutive interior (3x+15)° and (5x+5)°. Find x and angles.", answer: "x=20, 75° and 105°", solution: "Sum=180°" },
    { id: 58, question: "Areas 25 and 100, AB=7. Find DE.", answer: "14", solution: "Scale²=4, scale=2" },
    { id: 59, question: "cos(A)=5/13, AB=26. Find AC.", answer: "10", solution: "AC/26=5/13" },
    { id: 60, question: "Straight line: (2x+10)°, (3x-5)°, 75°. Find x.", answer: "20", solution: "Sum=180°" }
  ]
};